import Konva from "konva";
import { FONT_FAMILY, FONT_SIZE } from "./consts";

interface MeasureTextResult {
  width: number;
  height: number;
  isTruncated: boolean;
}

interface TextDimensionsResult {
  width: number;
  height: number;
  lineCount: number;
}

/**
 * Measures the width of text using Konva's text measurement.
 * Determines if text would be truncated when constrained to a maximum width.
 *
 * Note: This function only measures and reports truncation status.
 * Konva Text nodes handle ellipsis rendering automatically via the `ellipsis` prop.
 *
 * @param text - The text to measure
 * @param options - Configuration options
 * @param options.fontSize - Font size in pixels (defaults to FONT_SIZE constant)
 * @param options.fontFamily - Font family (defaults to FONT_FAMILY constant)
 * @param options.fontStyle - Font style like "500", "600", "normal", etc. (defaults to "normal")
 * @param options.maxWidth - Optional maximum width in pixels. If provided, checks if text would be truncated
 * @returns Object containing width and isTruncated flag
 *
 * @example
 * // Measure text width only
 * const { width } = measureTextWidth("Hello World");
 *
 * @example
 * // Check if text would be truncated at max width
 * const { width, isTruncated } = measureTextWidth("Very Long Name", { maxWidth: 100 });
 * // Then let Konva Text handle the ellipsis:
 * // <Text text="Very Long Name" width={100} ellipsis={true} />
 */
export function measureTextWidth(
  text: string,
  options: {
    fontSize?: number;
    fontFamily?: string;
    fontStyle?: string;
    maxWidth?: number;
    maxHeight?: number;
  } = {}
): MeasureTextResult {
  const { fontSize = FONT_SIZE, fontFamily = FONT_FAMILY, fontStyle = "normal", maxWidth, maxHeight } = options;

  // Create a temporary Konva.Text node for measurement
  const textNode = new Konva.Text({
    text,
    fontSize,
    fontFamily,
    fontStyle,
  });

  // Measure the full text
  const fullWidth = textNode.width();
  const fullHeight = textNode.height();

  // Clean up
  textNode.destroy();

  // If no maxWidth specified, return full width
  if (!maxWidth) {
    return {
      width: fullWidth,
      height: fullHeight,
      isTruncated: false,
    };
  }

  // Check if text would be truncated
  const isTruncated = fullWidth > maxWidth || (!!maxHeight && Number.isFinite(maxHeight) && fullHeight > maxHeight);

  return {
    width: Math.min(fullWidth, maxWidth),
    height: fullHeight,
    isTruncated,
  };
}

/**
 * Measures text dimensions (width, height, line count) using Konva's accurate text rendering.
 * Properly handles text wrapping and multi-line layout.
 *
 * Note: This function creates temporary Konva Text nodes to measure exact dimensions
 * including the effects of text wrapping at a specified maxWidth.
 *
 * @param text - The text to measure
 * @param options - Configuration options
 * @param options.fontSize - Font size in pixels (defaults to FONT_SIZE constant)
 * @param options.fontFamily - Font family (defaults to FONT_FAMILY constant)
 * @param options.fontStyle - Font style like "500", "600", "normal", etc. (defaults to "normal")
 * @param options.maxWidth - Maximum width in pixels for text wrapping. If provided, simulates word wrapping
 * @param options.wrap - Text wrapping mode: 'word' (default), 'char', or 'none'
 * @param options.lineHeight - Line height multiplier (defaults to 1.0)
 * @returns Object containing width, height, and number of lines
 *
 * @example
 * // Measure wrapped text dimensions for an expanded update
 * const { width, height, lineCount } = measureTextHeight(updateText, {
 *   maxWidth: 225, // Available width for update text
 *   wrap: 'word',
 *   fontSize: FONT_SIZE,
 * });
 *
 * @example
 * // Measure text with custom line height (useful for spacing)
 * const { height } = measureTextHeight("Long text here", {
 *   maxWidth: 200,
 *   fontSize: 14,
 *   lineHeight: 1.5,
 * });
 */
export function measureTextHeight(
  text: string,
  options: {
    fontSize?: number;
    fontFamily?: string;
    fontStyle?: string;
    maxWidth?: number;
    wrap?: "word" | "char" | "none";
    lineHeight?: number;
  } = {}
): TextDimensionsResult {
  const {
    fontSize = FONT_SIZE,
    fontFamily = FONT_FAMILY,
    fontStyle = "normal",
    maxWidth,
    wrap = "word",
    lineHeight = 1,
  } = options;

  // Create a temporary Konva.Text node for measurement
  const textNode = new Konva.Text({
    text,
    fontSize,
    fontFamily,
    fontStyle,
    ...(maxWidth && { width: maxWidth, wrap }),
  });

  // Get measured dimensions
  const measuredWidth = textNode.width();
  const measuredHeight = textNode.height();

  // Calculate line count based on actual height and font size
  // Konva's height includes line spacing, so divide by (fontSize * lineHeight)
  const calculatedLineHeight = fontSize * lineHeight;
  const lineCount = Math.max(1, Math.round(measuredHeight / calculatedLineHeight));

  // Clean up
  textNode.destroy();

  return {
    width: measuredWidth,
    height: measuredHeight,
    lineCount,
  };
}
